import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Gift, 
  Plus, 
  Edit, 
  Trash2, 
  Percent, 
  DollarSign, 
  Clock, 
  Star, 
  Award,
  Crown,
  Shield,
  Sparkles,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';

const BENEFIT_TYPES = [
  { value: 'discount_percentage', label: 'Discount (%)', icon: Percent },
  { value: 'discount_fixed', label: 'Fixed Discount ($)', icon: DollarSign },
  { value: 'free_service', label: 'Free Service', icon: Gift },
  { value: 'priority_access', label: 'Priority Access', icon: Star },
  { value: 'coupon', label: 'Coupon/Voucher', icon: Award },
  { value: 'other', label: 'Other Benefit', icon: Sparkles }
];

const EXPIRATION_PERIODS = [
  { value: 'month', label: 'Monthly' },
  { value: 'quarter', label: 'Quarterly' },
  { value: 'semester', label: 'Semester' },
  { value: 'year', label: 'Yearly' },
  { value: 'membership', label: 'Per Membership' }
];

const TIER_COLORS = {
  0: 'bg-green-100 text-green-800',      // Welcome
  3: 'bg-amber-100 text-amber-800',      // Bronze (0-3)
  6: 'bg-gray-200 text-gray-800',        // Silver (3-6)
  9: 'bg-yellow-100 text-yellow-800',    // Gold (6-9)
  12: 'bg-purple-100 text-purple-800',   // Platinum (9-12)
  13: 'bg-blue-100 text-blue-800'        // Diamond (+12)
};

// Tier breakpoints: 0-3, 3-6, 6-9, 9-12, +12
const getTierName = (months) => {
  if (months === 0) return 'Welcome';
  if (months <= 3) return 'Bronze';
  if (months <= 6) return 'Silver';
  if (months <= 9) return 'Gold';
  if (months <= 12) return 'Platinum';
  return 'Diamond';
};

const getTierLabel = (months) => {
  if (months === 0) return 'Immediate';
  if (months <= 3) return '0-3 months';
  if (months <= 6) return '3-6 months';
  if (months <= 9) return '6-9 months';
  if (months <= 12) return '9-12 months';
  return '+12 months';
};

const getTierIcon = (months) => {
  if (months === 0) return Gift;
  if (months <= 3) return Award;
  if (months <= 6) return Shield;
  if (months <= 9) return Star;
  if (months <= 12) return Crown;
  return Crown;
};

const BenefitsManagement = () => {
  const [benefits, setBenefits] = useState([]);
  const [loyaltyTiers, setLoyaltyTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    benefit_type: 'discount_percentage',
    value: '',
    expiration_period: 'year',
    min_membership_months: 0,
    max_uses_per_period: -1,
    applicable_to: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [benefitsRes, tiersRes] = await Promise.all([
        axios.get(`${API}/benefits/`),
        axios.get(`${API}/benefits/loyalty-tiers`)
      ]);
      setBenefits(benefitsRes.data);
      setLoyaltyTiers(tiersRes.data);
    } catch (error) {
      toast.error('Failed to load benefits');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      benefit_type: 'discount_percentage',
      value: '',
      expiration_period: 'year',
      min_membership_months: 0,
      max_uses_per_period: -1,
      applicable_to: '',
      is_active: true
    });
    setEditingBenefit(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        value: formData.value ? parseFloat(formData.value) : null,
        min_membership_months: parseInt(formData.min_membership_months) || 0,
        max_uses_per_period: parseInt(formData.max_uses_per_period) || -1
      };

      if (editingBenefit) {
        await axios.put(`${API}/benefits/${editingBenefit.id}`, payload);
        toast.success('Benefit updated successfully');
      } else {
        await axios.post(`${API}/benefits/`, payload);
        toast.success('Benefit created successfully');
      }
      
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save benefit');
    }
  };

  const handleEdit = (benefit) => {
    setEditingBenefit(benefit);
    setFormData({
      name: benefit.name,
      description: benefit.description || '',
      benefit_type: benefit.benefit_type,
      value: benefit.value?.toString() || '',
      expiration_period: benefit.expiration_period || 'year',
      min_membership_months: benefit.min_membership_months || 0,
      max_uses_per_period: benefit.max_uses_per_period || -1,
      applicable_to: benefit.applicable_to || '',
      is_active: benefit.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (benefit) => {
    if (!window.confirm(`Are you sure you want to delete "${benefit.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/benefits/${benefit.id}`);
      toast.success('Benefit deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete benefit');
    }
  };

  const getBenefitTypeInfo = (type) => {
    return BENEFIT_TYPES.find(t => t.value === type) || BENEFIT_TYPES[5];
  };

  const formatValue = (benefit) => {
    if (!benefit.value) return '-';
    switch (benefit.benefit_type) {
      case 'discount_percentage':
        return `${benefit.value}%`;
      case 'discount_fixed':
        return `$${benefit.value.toFixed(2)}`;
      default:
        return benefit.value;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Benefits Management</h2>
          <p className="text-gray-500">Manage loyalty benefits and rewards for your membership plans</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700">
                <Plus className="h-4 w-4 mr-2" />
                New Benefit
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingBenefit ? 'Edit Benefit' : 'Create New Benefit'}</DialogTitle>
                <DialogDescription>
                  Configure a benefit for your membership loyalty program
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Benefit Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., 20% Pharmacy Discount"
                    required
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe this benefit..."
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Benefit Type *</Label>
                    <Select 
                      value={formData.benefit_type} 
                      onValueChange={(v) => setFormData({...formData, benefit_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BENEFIT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <type.icon className="h-4 w-4" />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Value</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.value}
                      onChange={(e) => setFormData({...formData, value: e.target.value})}
                      placeholder={formData.benefit_type.includes('percentage') ? 'e.g., 20' : 'e.g., 50.00'}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Expiration Period</Label>
                    <Select 
                      value={formData.expiration_period} 
                      onValueChange={(v) => setFormData({...formData, expiration_period: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EXPIRATION_PERIODS.map((period) => (
                          <SelectItem key={period.value} value={period.value}>
                            {period.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Max Uses Per Period</Label>
                    <Input
                      type="number"
                      value={formData.max_uses_per_period === -1 ? '' : formData.max_uses_per_period}
                      onChange={(e) => setFormData({...formData, max_uses_per_period: e.target.value ? parseInt(e.target.value) : -1})}
                      placeholder="Unlimited"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty for unlimited</p>
                  </div>
                </div>

                <div className="p-4 bg-amber-50 rounded-lg">
                  <Label className="flex items-center gap-2 mb-3">
                    <Crown className="h-4 w-4 text-amber-600" />
                    Loyalty Tier Requirement
                  </Label>
                  <div className="grid grid-cols-5 gap-2 mb-3">
                    {[
                      { months: 0, label: 'Welcome' },
                      { months: 3, label: 'Bronze' },
                      { months: 6, label: 'Silver' },
                      { months: 9, label: 'Gold' },
                      { months: 12, label: 'Platinum' }
                    ].map((tier) => (
                      <Button
                        key={tier.months}
                        type="button"
                        size="sm"
                        variant={formData.min_membership_months === tier.months ? 'default' : 'outline'}
                        className={formData.min_membership_months === tier.months ? 'bg-amber-600 hover:bg-amber-700' : ''}
                        onClick={() => setFormData({...formData, min_membership_months: tier.months})}
                      >
                        {tier.label}
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-gray-600 whitespace-nowrap">Custom months:</Label>
                    <Input
                      type="number"
                      min="0"
                      max="24"
                      className="w-20"
                      value={formData.min_membership_months}
                      onChange={(e) => setFormData({...formData, min_membership_months: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <p className="text-xs text-amber-700 mt-2">
                    {formData.min_membership_months === 0 
                      ? '✓ Available immediately (Welcome Tier)'
                      : `✓ Unlocks at ${getTierName(formData.min_membership_months)} Tier (${getTierLabel(formData.min_membership_months)})`
                    }
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <Label>Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  />
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                    {editingBenefit ? 'Update' : 'Create'} Benefit
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="benefits" className="space-y-4">
        <TabsList>
          <TabsTrigger value="benefits">All Benefits</TabsTrigger>
          <TabsTrigger value="tiers">Loyalty Tiers</TabsTrigger>
        </TabsList>

        {/* Benefits List Tab */}
        <TabsContent value="benefits">
          <Card>
            <CardHeader>
              <CardTitle>Configured Benefits</CardTitle>
              <CardDescription>
                {benefits.length} benefit{benefits.length !== 1 ? 's' : ''} configured
              </CardDescription>
            </CardHeader>
            <CardContent>
              {benefits.length === 0 ? (
                <div className="text-center py-12">
                  <Gift className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No benefits configured yet</p>
                  <p className="text-sm text-gray-400">Create your first benefit to start building your loyalty program</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Benefit</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Tier</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {benefits.map((benefit) => {
                      const typeInfo = getBenefitTypeInfo(benefit.benefit_type);
                      const TierIcon = getTierIcon(benefit.min_membership_months);
                      return (
                        <TableRow key={benefit.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{benefit.name}</p>
                              {benefit.description && (
                                <p className="text-xs text-gray-500 truncate max-w-xs">{benefit.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <typeInfo.icon className="h-4 w-4 text-gray-500" />
                              <span className="text-sm">{typeInfo.label}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-semibold">
                            {formatValue(benefit)}
                          </TableCell>
                          <TableCell>
                            <Badge className={TIER_COLORS[benefit.min_membership_months] || TIER_COLORS[13] || 'bg-gray-100'}>
                              <TierIcon className="h-3 w-3 mr-1" />
                              {getTierName(benefit.min_membership_months)}
                            </Badge>
                            <p className="text-xs text-gray-500 mt-1">{getTierLabel(benefit.min_membership_months)}</p>
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {EXPIRATION_PERIODS.find(p => p.value === benefit.expiration_period)?.label || benefit.expiration_period}
                          </TableCell>
                          <TableCell>
                            <Badge className={benefit.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                              {benefit.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="ghost" onClick={() => handleEdit(benefit)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(benefit)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Loyalty Tiers Tab */}
        <TabsContent value="tiers">
          <div className="grid gap-4">
            {loyaltyTiers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Crown className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No loyalty tiers configured</p>
                  <p className="text-sm text-gray-400">Create benefits with different membership requirements to build tiers</p>
                </CardContent>
              </Card>
            ) : (
              loyaltyTiers.map((tier) => {
                const TierIcon = getTierIcon(tier.months_required);
                return (
                  <Card key={tier.months_required} className="border-l-4 border-l-teal-500">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          <TierIcon className="h-5 w-5 text-teal-600" />
                          {tier.tier_name}
                        </CardTitle>
                        <Badge variant="outline">
                          {tier.months_required === 0 
                            ? 'Immediate Access' 
                            : `After ${tier.months_required} months`
                          }
                        </Badge>
                      </div>
                      <CardDescription>
                        {tier.benefits.length} benefit{tier.benefits.length !== 1 ? 's' : ''} in this tier
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-2">
                        {tier.benefits.map((benefit) => {
                          const typeInfo = getBenefitTypeInfo(benefit.type);
                          return (
                            <div 
                              key={benefit.id}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                                  <typeInfo.icon className="h-4 w-4 text-teal-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{benefit.name}</p>
                                  {benefit.description && (
                                    <p className="text-xs text-gray-500">{benefit.description}</p>
                                  )}
                                </div>
                              </div>
                              {benefit.value && (
                                <Badge variant="secondary">
                                  {benefit.type === 'discount_percentage' ? `${benefit.value}%` : 
                                   benefit.type === 'discount_fixed' ? `$${benefit.value}` : benefit.value}
                                </Badge>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BenefitsManagement;
